import { useEffect, useState } from 'react';
import { shuffledRestaurants } from '../data/restaurants.js';
import { fetchRestaurants } from '../lib/api.js';
import { getDeviceId } from '../lib/identity.js';
import { useCoarseLocation } from './useCoarseLocation.js';

/**
 * The restaurants the app swipes through.
 *
 * Real venues near the person, discovered from Google around wherever they
 * happen to be. Location is asked for but never required: without it the server
 * returns the stored catalogue instead, which is still real restaurants, just
 * not necessarily nearby ones.
 *
 * The bundled fictional venues are a last resort only — reached when there is
 * no database or no Places key, which is the fresh-clone case. They are
 * deliberately not the initial state any more: showing invented restaurants for
 * a moment before the real ones arrive would let someone act on a place that
 * does not exist.
 *
 * @param {{skipLocation?: boolean}} [options]
 * @returns {{
 *   catalogue: import('../lib/types.js').Restaurant[],
 *   status: 'loading'|'ready'|'fallback',
 *   locationStatus: 'idle'|'granted'|'denied',
 *   coords: [number, number] | null,
 * }}
 */
export function useCatalogue({ skipLocation = false } = {}) {
  const { coords, status: locationStatus } = useCoarseLocation();
  const [catalogue, setCatalogue] = useState([]);
  const [status, setStatus] = useState('loading');

  const effectiveLocationStatus = skipLocation ? 'denied' : locationStatus;
  const effectiveCoords = skipLocation ? null : coords;

  useEffect(() => {
    // Wait until the location question has been answered either way, so the
    // first request already carries coordinates when they are coming.
    if (effectiveLocationStatus === 'idle') return undefined;

    let active = true;

    fetchRestaurants({ deviceId: getDeviceId(), coords: effectiveCoords }).then((restaurants) => {
      if (!active) return;

      if (restaurants) {
        // Server order is the recommendation, so it is kept as it arrives.
        setCatalogue(restaurants);
        setStatus('ready');
      } else {
        setCatalogue(shuffledRestaurants());
        setStatus('fallback');
      }
    });

    return () => {
      active = false;
    };
  }, [effectiveCoords, effectiveLocationStatus]);

  return {
    catalogue,
    status,
    locationStatus: effectiveLocationStatus,
    coords: effectiveCoords,
  };
}
